import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, ZoomIn, X, ChevronLeft, ChevronRight } from 'lucide-react';

/* ─── Types ─── */
interface Screenshot {
  id: string;
  category: 'gestion' | 'evaluacion' | 'boletines' | 'configuracion';
  title: string;
  caption: string;
  image: string;
}

/* ─── Scroll-reveal hook ─── */
function useReveal(threshold = 0.15) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setVisible(true); obs.disconnect(); } },
      { threshold }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);

  return { ref, visible };
}

/* ─── Component ─── */
export function LandingPage() {
  const navigate = useNavigate();
  const [lightbox, setLightbox] = useState<string | null>(null);
  const [activeCategory, setActiveCategory] = useState<Screenshot['category']>('gestion');
  const [activeScreenshot, setActiveScreenshot] = useState(0);
  const [headerSolid, setHeaderSolid] = useState(false);

  // Scroll-based header opacity
  useEffect(() => {
    const onScroll = () => setHeaderSolid(window.scrollY > 60);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Font injection
  useEffect(() => {
    if (!document.getElementById('lp-fonts')) {
      const link = document.createElement('link');
      link.id = 'lp-fonts';
      link.rel = 'stylesheet';
      link.href = 'https://fonts.googleapis.com/css2?family=DM+Serif+Display:ital@0;1&family=Source+Sans+3:wght@300;400;500;600;700&display=swap';
      document.head.appendChild(link);
    }
  }, []);

  /* ─── Screenshots catalogue ─── */
  const screenshots: Screenshot[] = [
    // Gestión
    { id: 'inicio', category: 'gestion', title: 'Panel de Inicio', caption: 'Vista general con accesos directos a cada módulo del sistema.', image: '/images/inicio.png' },
    { id: 'gestion-de-centros', category: 'gestion', title: 'Gestión Multicentro', caption: 'Administración de múltiples centros desde un único portal.', image: '/images/gestion-de-centros.png' },
    { id: 'gestion-profesorado', category: 'gestion', title: 'Equipo Docente', caption: 'Fichas del profesorado con especialidades y permisos.', image: '/images/gestion-profesorado.png' },
    { id: 'gestion-asignaturas-profesorado', category: 'gestion', title: 'Asignación de Materias', caption: 'Asigna asignaturas a cada profesor de forma visual.', image: '/images/gestion-asignaturas.profesorado.png' },
    { id: 'gestion-alumnados', category: 'gestion', title: 'Expedientes del Alumnado', caption: 'Ficha individual con historial y tutores legales.', image: '/images/gestion-alumnados.png' },
    // Evaluación
    { id: 'introduccion-de-notas', category: 'evaluacion', title: 'Planilla de Notas', caption: 'Introducción de calificaciones vinculadas a criterios LOMLOE.', image: '/images/introduccion-de-notas.png' },
    { id: 'configuracion-criterios-evaluacion', category: 'evaluacion', title: 'Criterios LOMLOE', caption: 'Competencias y criterios por materia y curso.', image: '/images/configuracion-criterios-evaluacion.png' },
    { id: 'configuracion-instrumentos', category: 'evaluacion', title: 'Instrumentos de Evaluación', caption: 'Exámenes, trabajos y sus ponderaciones.', image: '/images/configuracion-instrumentos.png' },
    { id: 'evolucion-notas-historial', category: 'evaluacion', title: 'Evolución y Estadísticas', caption: 'Progresión de rendimiento por alumno y asignatura.', image: '/images/evolucion-notas-historial.png' },
    // Boletines
    { id: 'boletines-plantillas', category: 'boletines', title: 'Diseñador de Boletines', caption: 'Plantillas oficiales para los boletines de notas.', image: '/images/boletines-plantillas.png' },
    { id: 'configuracion-plantillas-curso', category: 'boletines', title: 'Plantillas por Curso', caption: 'Asigna un modelo de boletín a cada curso.', image: '/images/configuracion-plantillas-curso.png' },
    { id: 'configuracion-plantillas-correo-electronico', category: 'boletines', title: 'Plantillas de Correo', caption: 'Mensajes automatizados con etiquetas dinámicas.', image: '/images/configuracion-plantillas-correo-electronico.png' },
    { id: 'configuracion-firma-digital', category: 'boletines', title: 'Firma Digital', caption: 'Firma criptográfica para garantizar la autenticidad.', image: '/images/configuracion-firma-digital.png' },
    { id: 'configuracion-historial-envios', category: 'boletines', title: 'Historial de Envíos', caption: 'Trazabilidad de cada correo enviado a las familias.', image: '/images/configuracion-historial-envios.png' },
    // Configuración
    { id: 'configuracion-centro', category: 'configuracion', title: 'Datos del Centro', caption: 'Ficha institucional, logotipo e idioma.', image: '/images/configuracion-centro.png' },
    { id: 'guia-configuracion-centro', category: 'configuracion', title: 'Asistente de Inicio', caption: 'Guía paso a paso para la puesta en marcha.', image: '/images/guia-configuracion-centro.png' },
    { id: 'configuracion-anos-academicos', category: 'configuracion', title: 'Años Académicos', caption: 'Periodos anuales con histórico de datos.', image: '/images/configuracion-anos-academicos.png' },
    { id: 'configuracion-periodos-evaluacion', category: 'configuracion', title: 'Periodos de Evaluación', caption: 'Trimestres, semestres y fechas límite.', image: '/images/configuracion-periodos-evaluacion.png' },
    { id: 'configuracion-cursos', category: 'configuracion', title: 'Cursos', caption: 'Estructura de cursos y aulas por año.', image: '/images/configuracion-cursos.png' },
    { id: 'configuracion-niveles', category: 'configuracion', title: 'Niveles Educativos', caption: 'Niveles oficiales de formación del centro.', image: '/images/configuracion-niveles.png' },
    { id: 'configuracion-pueblos-cra', category: 'configuracion', title: 'Pueblos del CRA', caption: 'Gestión de secciones de Colegios Rurales Agrupados.', image: '/images/configuracion-pueblos-cra.png' },
    { id: 'configuracion-asignaturas-grupales', category: 'configuracion', title: 'Asignaturas Grupales', caption: 'Materias colectivas con registro conjunto.', image: '/images/configuracion-asignaturas-grupales.png' },
    { id: 'cursos-niveles-instrumentos', category: 'configuracion', title: 'Estructura del Plan', caption: 'Relación entre cursos, niveles e instrumentos.', image: '/images/cursos-niveles-instrumentos.png' },
    { id: 'configuracion-importar-configuracion', category: 'configuracion', title: 'Importación Masiva', caption: 'Carga de datos desde CSV y Excel.', image: '/images/configuracion-importar-configuracion.png' },
  ];

  const filtered = screenshots.filter(s => s.category === activeCategory);
  
  // Reset active when changing category
  useEffect(() => { setActiveScreenshot(0); }, [activeCategory]);

  const current = filtered[activeScreenshot] || filtered[0];

  const nextScreenshot = useCallback(() => {
    setActiveScreenshot(prev => (prev + 1) % filtered.length);
  }, [filtered.length]);

  const prevScreenshot = useCallback(() => {
    setActiveScreenshot(prev => (prev - 1 + filtered.length) % filtered.length);
  }, [filtered.length]);

  // Reveal hooks for sections
  const heroReveal = useReveal(0.1);
  const pillarReveal = useReveal(0.1);
  const showcase1 = useReveal(0.12);
  const showcase2 = useReveal(0.12);
  const showcase3 = useReveal(0.12);
  const explorerReveal = useReveal(0.08);
  const ctaReveal = useReveal(0.15);

  const categories = [
    { id: 'gestion' as const, label: 'Gestión Escolar' },
    { id: 'evaluacion' as const, label: 'Evaluación LOMLOE' },
    { id: 'boletines' as const, label: 'Boletines y Envíos' },
    { id: 'configuracion' as const, label: 'Configuración' },
  ];

  return (
    <div className="landing-root">
      <style>{`
        /* ── Design System ── */
        :root {
          --lp-navy: #1e293b;
          --lp-navy-light: #293548;
          --lp-accent: #4f46e5;
          --lp-accent-soft: #6366f1;
          --lp-cream: #f8fafc;
          --lp-warm-gray: #64748b;
          --lp-text: #1e293b;
          --lp-text-secondary: #64748b;
          --lp-border: #e2e8f0;
          --lp-bg-alt: #f1f5f9;
        }
        .landing-root {
          font-family: 'Source Sans 3', 'Segoe UI', sans-serif;
          color: var(--lp-text);
          background: var(--lp-cream);
          -webkit-font-smoothing: antialiased;
          overflow-x: hidden;
        }
        .font-display {
          font-family: 'DM Serif Display', Georgia, serif;
        }

        /* ── Animations ── */
        .reveal-up {
          opacity: 0;
          transform: translateY(32px);
          transition: opacity 0.7s cubic-bezier(0.22, 1, 0.36, 1), transform 0.7s cubic-bezier(0.22, 1, 0.36, 1);
        }
        .reveal-up.is-visible {
          opacity: 1;
          transform: translateY(0);
        }
        .reveal-left {
          opacity: 0;
          transform: translateX(-40px);
          transition: opacity 0.7s cubic-bezier(0.22, 1, 0.36, 1), transform 0.7s cubic-bezier(0.22, 1, 0.36, 1);
        }
        .reveal-left.is-visible {
          opacity: 1;
          transform: translateX(0);
        }
        .reveal-right {
          opacity: 0;
          transform: translateX(40px);
          transition: opacity 0.7s cubic-bezier(0.22, 1, 0.36, 1), transform 0.7s cubic-bezier(0.22, 1, 0.36, 1);
        }
        .reveal-right.is-visible {
          opacity: 1;
          transform: translateX(0);
        }
        .reveal-scale {
          opacity: 0;
          transform: scale(0.95);
          transition: opacity 0.6s ease, transform 0.6s ease;
        }
        .reveal-scale.is-visible {
          opacity: 1;
          transform: scale(1);
        }
        .stagger-1 { transition-delay: 0.08s; }
        .stagger-2 { transition-delay: 0.16s; }
        .stagger-3 { transition-delay: 0.24s; }
        .stagger-4 { transition-delay: 0.32s; }

        /* ── Screenshot frame ── */
        .app-frame {
          background: #fff;
          border: 1px solid var(--lp-border);
          border-radius: 12px;
          overflow: hidden;
          box-shadow: 0 1px 3px rgba(0,0,0,0.04), 0 8px 24px rgba(0,0,0,0.06);
          transition: box-shadow 0.3s ease, transform 0.3s ease;
        }
        .app-frame:hover {
          box-shadow: 0 2px 6px rgba(0,0,0,0.06), 0 12px 32px rgba(0,0,0,0.1);
          transform: translateY(-2px);
        }
        .app-frame img {
          width: 100%;
          height: auto;
          display: block;
        }

        /* ── Button styles ── */
        .btn-primary {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 14px 28px;
          background: var(--lp-navy);
          color: #fff;
          font-family: 'Source Sans 3', sans-serif;
          font-size: 14px;
          font-weight: 600;
          letter-spacing: 0.3px;
          border: none;
          border-radius: 8px;
          cursor: pointer;
          transition: background 0.2s ease, transform 0.15s ease;
        }
        .btn-primary:hover {
          background: var(--lp-navy-light);
          transform: translateY(-1px);
        }
        .btn-ghost {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 14px 28px;
          background: transparent;
          color: var(--lp-navy);
          font-family: 'Source Sans 3', sans-serif;
          font-size: 14px;
          font-weight: 600;
          border: 1.5px solid var(--lp-border);
          border-radius: 8px;
          cursor: pointer;
          transition: border-color 0.2s ease, background 0.2s ease;
        }
        .btn-ghost:hover {
          border-color: var(--lp-navy);
          background: rgba(26,35,50,0.03);
        }

        /* ── Explorer tabs ── */
        .cat-tab {
          padding: 10px 20px;
          font-size: 13px;
          font-weight: 600;
          color: var(--lp-text-secondary);
          background: transparent;
          border: 1.5px solid transparent;
          border-radius: 8px;
          cursor: pointer;
          transition: all 0.2s ease;
          white-space: nowrap;
        }
        .cat-tab:hover {
          color: var(--lp-text);
          background: rgba(0,0,0,0.03);
        }
        .cat-tab.active {
          color: var(--lp-navy);
          background: #fff;
          border-color: var(--lp-border);
          box-shadow: 0 1px 4px rgba(0,0,0,0.06);
        }

        /* ── Thumbnail strip ── */
        .thumb-strip {
          display: flex;
          gap: 6px;
          overflow-x: auto;
          padding: 4px 0;
          scrollbar-width: none;
        }
        .thumb-strip::-webkit-scrollbar { display: none; }
        .thumb-item {
          flex-shrink: 0;
          width: 72px;
          height: 48px;
          border-radius: 6px;
          overflow: hidden;
          cursor: pointer;
          border: 2px solid transparent;
          opacity: 0.55;
          transition: opacity 0.2s ease, border-color 0.2s ease, transform 0.15s ease;
        }
        .thumb-item:hover {
          opacity: 0.85;
          transform: scale(1.05);
        }
        .thumb-item.active {
          opacity: 1;
          border-color: var(--lp-accent);
        }
        .thumb-item img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }

        /* ── Lightbox ── */
        .lightbox-overlay {
          position: fixed;
          inset: 0;
          z-index: 100;
          background: rgba(26,35,50,0.88);
          backdrop-filter: blur(8px);
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 24px;
          animation: lbFadeIn 0.25s ease;
        }
        @keyframes lbFadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        .lightbox-content {
          position: relative;
          max-width: 1100px;
          width: 100%;
          background: #fff;
          border-radius: 12px;
          overflow: hidden;
          box-shadow: 0 24px 80px rgba(0,0,0,0.3);
        }
        .lightbox-content img {
          width: 100%;
          height: auto;
          display: block;
          max-height: 80vh;
          object-fit: contain;
          background: #f8f7f5;
        }
        .lightbox-close {
          position: absolute;
          top: 12px;
          right: 12px;
          width: 36px;
          height: 36px;
          background: rgba(255,255,255,0.9);
          border: 1px solid var(--lp-border);
          border-radius: 8px;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: background 0.15s ease;
          z-index: 2;
        }
        .lightbox-close:hover {
          background: #fff;
        }

        /* ── Pillar cards ── */
        .pillar-card {
          padding: 32px 28px;
          background: #fff;
          border: 1px solid var(--lp-border);
          border-radius: 12px;
          transition: transform 0.25s ease, box-shadow 0.25s ease;
        }
        .pillar-card:hover {
          transform: translateY(-3px);
          box-shadow: 0 6px 20px rgba(0,0,0,0.06);
        }
        .pillar-number {
          font-family: 'DM Serif Display', serif;
          font-size: 36px;
          color: var(--lp-accent);
          line-height: 1;
          margin-bottom: 16px;
        }
        .pillar-title {
          font-family: 'DM Serif Display', serif;
          font-size: 20px;
          color: var(--lp-navy);
          margin-bottom: 10px;
          line-height: 1.3;
        }
        .pillar-desc {
          font-size: 14px;
          color: var(--lp-text-secondary);
          line-height: 1.65;
        }

        /* ── Showcase section ── */
        .showcase-section {
          padding: 80px 0;
        }
        .showcase-section:nth-child(even) {
          background: var(--lp-bg-alt);
        }
        .showcase-tag {
          display: inline-block;
          font-size: 11px;
          font-weight: 700;
          letter-spacing: 1.2px;
          text-transform: uppercase;
          color: var(--lp-accent);
          margin-bottom: 16px;
        }
        .showcase-heading {
          font-family: 'DM Serif Display', serif;
          font-size: clamp(26px, 3.5vw, 36px);
          color: var(--lp-navy);
          line-height: 1.2;
          margin-bottom: 16px;
        }
        .showcase-body {
          font-size: 15px;
          line-height: 1.7;
          color: var(--lp-text-secondary);
          max-width: 480px;
        }
        .showcase-list {
          list-style: none;
          padding: 0;
          margin: 20px 0 0;
        }
        .showcase-list li {
          position: relative;
          padding-left: 18px;
          margin-bottom: 10px;
          font-size: 14px;
          color: var(--lp-text-secondary);
          line-height: 1.55;
        }
        .showcase-list li::before {
          content: '';
          position: absolute;
          left: 0;
          top: 8px;
          width: 6px;
          height: 6px;
          background: var(--lp-accent);
          border-radius: 50%;
        }

        /* ── Nav arrow buttons ── */
        .nav-arrow {
          width: 40px;
          height: 40px;
          background: #fff;
          border: 1px solid var(--lp-border);
          border-radius: 8px;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: background 0.15s ease, border-color 0.15s ease;
          color: var(--lp-text);
        }
        .nav-arrow:hover {
          background: var(--lp-bg-alt);
          border-color: #ccc;
        }

        /* ── Responsive ── */
        @media (max-width: 768px) {
          .showcase-grid { flex-direction: column !important; }
          .showcase-grid.reverse { flex-direction: column !important; }
          .explorer-layout { flex-direction: column !important; }
        }
      `}</style>

      {/* ═══════ HEADER ═══════ */}
      <header
        style={{
          position: 'sticky',
          top: 0,
          zIndex: 50,
          background: headerSolid ? 'rgba(250,248,244,0.95)' : 'transparent',
          backdropFilter: headerSolid ? 'blur(12px)' : 'none',
          borderBottom: headerSolid ? '1px solid var(--lp-border)' : '1px solid transparent',
          transition: 'all 0.3s ease',
        }}
      >
        <div style={{ maxWidth: 1140, margin: '0 auto', padding: '0 32px', height: 72, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <img src="/icon.png" alt="Boletines" style={{ width: 36, height: 36, borderRadius: 8 }} />
            <div>
              <div className="font-display" style={{ fontSize: 18, color: 'var(--lp-navy)', fontWeight: 400, lineHeight: 1.1 }}>Boletines</div>
              <div style={{ fontSize: 11, color: 'var(--lp-text-secondary)', letterSpacing: '0.5px' }}>Gestión Académica</div>
            </div>
          </div>
          <button className="btn-primary" onClick={() => navigate('/login')}>
            Acceso Docente <ArrowRight size={15} />
          </button>
        </div>
      </header>

      {/* ═══════ HERO ═══════ */}
      <section
        ref={heroReveal.ref}
        style={{ padding: '64px 0 80px', background: 'var(--lp-cream)' }}
      >
        <div style={{ maxWidth: 1140, margin: '0 auto', padding: '0 32px', display: 'flex', gap: 48, alignItems: 'center', flexWrap: 'wrap' }}>
          {/* Text side */}
          <div className={`reveal-up ${heroReveal.visible ? 'is-visible' : ''}`} style={{ flex: '1 1 440px', minWidth: 320 }}>
            <p style={{ fontSize: 13, fontWeight: 600, letterSpacing: '1px', textTransform: 'uppercase', color: 'var(--lp-accent)', marginBottom: 20 }}>
              Plataforma de gestión docente
            </p>
            <h1 className="font-display" style={{ fontSize: 'clamp(32px, 5vw, 52px)', color: 'var(--lp-navy)', lineHeight: 1.12, marginBottom: 24 }}>
              La evaluación escolar,{' '}
              <em style={{ fontStyle: 'italic', color: 'var(--lp-accent)' }}>simplificada</em>.
            </h1>
            <p style={{ fontSize: 17, lineHeight: 1.7, color: 'var(--lp-text-secondary)', maxWidth: 500, marginBottom: 32 }}>
              Diseñada para conservatorios y escuelas de música que necesitan gestionar notas, adaptar criterios LOMLOE y emitir boletines oficiales firmados digitalmente — sin complicaciones.
            </p>
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
              <button className="btn-primary" onClick={() => navigate('/login')}>
                Entrar al portal <ArrowRight size={15} />
              </button>
              <button className="btn-ghost" onClick={() => document.getElementById('explorador')?.scrollIntoView({ behavior: 'smooth' })}>
                Ver la plataforma
              </button>
            </div>
          </div>

          {/* Hero image */}
          <div className={`reveal-right ${heroReveal.visible ? 'is-visible' : ''}`} style={{ flex: '1 1 480px', minWidth: 320 }}>
            <div className="app-frame" style={{ cursor: 'pointer' }} onClick={() => setLightbox('/images/inicio.png')}>
              <div style={{ position: 'relative' }}>
                <img src="/images/inicio.png" alt="Panel de control de Boletines" />
                <div style={{
                  position: 'absolute', inset: 0, background: 'rgba(26,35,50,0.03)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  opacity: 0, transition: 'opacity 0.2s ease',
                }}
                  onMouseEnter={e => (e.currentTarget.style.opacity = '1')}
                  onMouseLeave={e => (e.currentTarget.style.opacity = '0')}
                >
                  <span style={{ background: '#fff', padding: '8px 16px', borderRadius: 6, fontSize: 12, fontWeight: 600, color: 'var(--lp-navy)', display: 'flex', alignItems: 'center', gap: 6, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
                    <ZoomIn size={14} /> Ampliar
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ═══════ PILARES ═══════ */}
      <section style={{ padding: '80px 0', background: '#fff', borderTop: '1px solid var(--lp-border)', borderBottom: '1px solid var(--lp-border)' }}>
        <div ref={pillarReveal.ref} style={{ maxWidth: 1140, margin: '0 auto', padding: '0 32px' }}>
          <div className={`reveal-up ${pillarReveal.visible ? 'is-visible' : ''}`} style={{ maxWidth: 560, marginBottom: 48 }}>
            <p className="showcase-tag">Fundamentos</p>
            <h2 className="font-display" style={{ fontSize: 'clamp(26px, 3.5vw, 38px)', color: 'var(--lp-navy)', lineHeight: 1.15, marginBottom: 12 }}>
              Cuatro pilares para la gestión docente
            </h2>
            <p style={{ fontSize: 15, color: 'var(--lp-text-secondary)', lineHeight: 1.65 }}>
              Todo lo que necesita un centro educativo para administrar notas, profesorado y comunicación con las familias.
            </p>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 16 }}>
            {[
              { n: '01', title: 'Administración Escolar', desc: 'Gestión de centros, profesorado, alumnado y asignaturas con fichas individuales y control de permisos.' },
              { n: '02', title: 'Evaluación LOMLOE', desc: 'Planilla interactiva de calificaciones con ponderación automática de criterios e instrumentos evaluativos.' },
              { n: '03', title: 'Boletines Oficiales', desc: 'Generación de boletines en PDF con diseño editorial, firma digital y envío masivo por correo electrónico.' },
              { n: '04', title: 'Configuración Modular', desc: 'Ajustes de centro, años académicos, periodos, niveles, cursos, CRA e importación masiva de datos.' },
            ].map((p, i) => (
              <div
                key={p.n}
                className={`pillar-card reveal-up stagger-${i + 1} ${pillarReveal.visible ? 'is-visible' : ''}`}
              >
                <div className="pillar-number">{p.n}</div>
                <div className="pillar-title">{p.title}</div>
                <div className="pillar-desc">{p.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════ SHOWCASE 1: Gestión ═══════ */}
      <section className="showcase-section" ref={showcase1.ref}>
        <div style={{ maxWidth: 1140, margin: '0 auto', padding: '0 32px' }}>
          <div className="showcase-grid" style={{ display: 'flex', gap: 48, alignItems: 'center', flexWrap: 'wrap' }}>
            <div className={`reveal-left ${showcase1.visible ? 'is-visible' : ''}`} style={{ flex: '1 1 420px', minWidth: 300 }}>
              <span className="showcase-tag">Administración Escolar</span>
              <h2 className="showcase-heading">Control completo del centro y equipo docente</h2>
              <p className="showcase-body">
                Centraliza la gestión de expedientes, profesorado y asignación de materias en una base de datos segura. Incluye soporte multicentro para administrar campus independientes.
              </p>
              <ul className="showcase-list">
                <li>Fichas individuales del alumnado con historial de matrículas</li>
                <li>Gestión de profesores, especialidades, permisos y tutorías</li>
                <li>Asignación visual de asignaturas a cada docente</li>
                <li>Administración remota de múltiples centros o campus</li>
              </ul>
            </div>
            <div className={`reveal-right ${showcase1.visible ? 'is-visible' : ''}`} style={{ flex: '1 1 480px', minWidth: 300 }}>
              <div className="app-frame" style={{ cursor: 'pointer' }} onClick={() => setLightbox('/images/gestion-alumnados.png')}>
                <img src="/images/gestion-alumnados.png" alt="Gestión del alumnado" style={{ maxHeight: 380, objectFit: 'cover' }} />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ═══════ SHOWCASE 2: Evaluación ═══════ */}
      <section className="showcase-section" ref={showcase2.ref}>
        <div style={{ maxWidth: 1140, margin: '0 auto', padding: '0 32px' }}>
          <div className="showcase-grid reverse" style={{ display: 'flex', gap: 48, alignItems: 'center', flexWrap: 'wrap', flexDirection: 'row-reverse' }}>
            <div className={`reveal-right ${showcase2.visible ? 'is-visible' : ''}`} style={{ flex: '1 1 420px', minWidth: 300 }}>
              <span className="showcase-tag">Evaluación LOMLOE</span>
              <h2 className="showcase-heading">Planilla de notas interactiva y conforme</h2>
              <p className="showcase-body">
                Olvídate de las hojas de cálculo. La planilla realiza automáticamente las ponderaciones de criterios e instrumentos, cumpliendo la directiva LOMLOE vigente.
              </p>
              <ul className="showcase-list">
                <li>Introducción de notas vinculada a criterios de evaluación</li>
                <li>Asociación directa de exámenes, audiciones y trabajos</li>
                <li>Cálculo automático de ponderaciones y promedios</li>
                <li>Gráficos de evolución del rendimiento por estudiante</li>
              </ul>
            </div>
            <div className={`reveal-left ${showcase2.visible ? 'is-visible' : ''}`} style={{ flex: '1 1 480px', minWidth: 300 }}>
              <div className="app-frame" style={{ cursor: 'pointer' }} onClick={() => setLightbox('/images/introduccion-de-notas.png')}>
                <img src="/images/introduccion-de-notas.png" alt="Planilla de calificaciones" style={{ maxHeight: 380, objectFit: 'cover' }} />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ═══════ SHOWCASE 3: Boletines ═══════ */}
      <section className="showcase-section" ref={showcase3.ref}>
        <div style={{ maxWidth: 1140, margin: '0 auto', padding: '0 32px' }}>
          <div className="showcase-grid" style={{ display: 'flex', gap: 48, alignItems: 'center', flexWrap: 'wrap' }}>
            <div className={`reveal-left ${showcase3.visible ? 'is-visible' : ''}`} style={{ flex: '1 1 420px', minWidth: 300 }}>
              <span className="showcase-tag">Boletines y Comunicación</span>
              <h2 className="showcase-heading">Boletines oficiales con firma digital integrada</h2>
              <p className="showcase-body">
                Genera boletines en PDF con diseño editorial profesional, firma criptográfica embebida y envío masivo a las familias con historial de trazabilidad completo.
              </p>
              <ul className="showcase-list">
                <li>Diseño editorial limpio para boletines impresos y digitales</li>
                <li>Firma digital que avala y blinda criptográficamente las notas</li>
                <li>Envío masivo directo al correo de las familias</li>
                <li>Historial de envíos con confirmación de recepción</li>
              </ul>
            </div>
            <div className={`reveal-right ${showcase3.visible ? 'is-visible' : ''}`} style={{ flex: '1 1 480px', minWidth: 300 }}>
              <div className="app-frame" style={{ cursor: 'pointer' }} onClick={() => setLightbox('/images/boletines-plantillas.png')}>
                <img src="/images/boletines-plantillas.png" alt="Plantillas de boletines" style={{ maxHeight: 380, objectFit: 'cover' }} />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ═══════ EXPLORADOR DE CAPTURAS ═══════ */}
      <section id="explorador" style={{ padding: '80px 0', background: '#fff', borderTop: '1px solid var(--lp-border)' }}>
        <div ref={explorerReveal.ref} style={{ maxWidth: 1140, margin: '0 auto', padding: '0 32px' }}>
          <div className={`reveal-up ${explorerReveal.visible ? 'is-visible' : ''}`} style={{ maxWidth: 560, marginBottom: 40 }}>
            <span className="showcase-tag">Explorador de la plataforma</span>
            <h2 className="font-display" style={{ fontSize: 'clamp(26px, 3.5vw, 36px)', color: 'var(--lp-navy)', lineHeight: 1.2, marginBottom: 12 }}>
              Cada rincón de la aplicación, a un clic
            </h2>
            <p style={{ fontSize: 15, color: 'var(--lp-text-secondary)', lineHeight: 1.65 }}>
              Selecciona una sección para explorar las pantallas y funcionalidades disponibles.
            </p>
          </div>

          {/* Category tabs */}
          <div className={`reveal-up stagger-1 ${explorerReveal.visible ? 'is-visible' : ''}`} style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 32 }}>
            {categories.map(c => (
              <button
                key={c.id}
                className={`cat-tab ${activeCategory === c.id ? 'active' : ''}`}
                onClick={() => setActiveCategory(c.id)}
              >
                {c.label}
              </button>
            ))}
          </div>

          {/* Explorer layout */}
          <div
            className={`explorer-layout reveal-up stagger-2 ${explorerReveal.visible ? 'is-visible' : ''}`}
            style={{ display: 'flex', gap: 24, alignItems: 'flex-start', flexWrap: 'wrap' }}
          >
            {/* Main viewer */}
            <div style={{ flex: '1 1 640px', minWidth: 300 }}>
              <div className="app-frame" style={{ cursor: 'pointer', position: 'relative' }} onClick={() => setLightbox(current.image)}>
                <img
                  src={current.image}
                  alt={current.title}
                  style={{ maxHeight: 480, objectFit: 'cover', width: '100%' }}
                />
                <div style={{
                  position: 'absolute', inset: 0, background: 'rgba(26,35,50,0.03)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  opacity: 0, transition: 'opacity 0.2s ease',
                }}
                  onMouseEnter={e => (e.currentTarget.style.opacity = '1')}
                  onMouseLeave={e => (e.currentTarget.style.opacity = '0')}
                >
                  <span style={{ background: '#fff', padding: '8px 16px', borderRadius: 6, fontSize: 12, fontWeight: 600, color: 'var(--lp-navy)', display: 'flex', alignItems: 'center', gap: 6, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
                    <ZoomIn size={14} /> Ampliar
                  </span>
                </div>
              </div>

              {/* Caption + navigation */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 16, gap: 12 }}>
                <div>
                  <div className="font-display" style={{ fontSize: 18, color: 'var(--lp-navy)', marginBottom: 4 }}>{current.title}</div>
                  <div style={{ fontSize: 13, color: 'var(--lp-text-secondary)' }}>{current.caption}</div>
                </div>
                <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                  <button className="nav-arrow" onClick={prevScreenshot}><ChevronLeft size={18} /></button>
                  <button className="nav-arrow" onClick={nextScreenshot}><ChevronRight size={18} /></button>
                </div>
              </div>

              {/* Thumbnail strip */}
              <div className="thumb-strip" style={{ marginTop: 16 }}>
                {filtered.map((s, i) => (
                  <div
                    key={s.id}
                    className={`thumb-item ${i === activeScreenshot ? 'active' : ''}`}
                    onClick={() => setActiveScreenshot(i)}
                  >
                    <img src={s.image} alt={s.title} />
                  </div>
                ))}
              </div>
            </div>

            {/* Side list */}
            <div style={{ flex: '0 0 280px', minWidth: 240 }}>
              <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '1px', textTransform: 'uppercase', color: 'var(--lp-text-secondary)', marginBottom: 12 }}>
                {filtered.length} pantallas
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                {filtered.map((s, i) => (
                  <button
                    key={s.id}
                    onClick={() => setActiveScreenshot(i)}
                    style={{
                      textAlign: 'left',
                      padding: '10px 14px',
                      borderRadius: 8,
                      border: 'none',
                      cursor: 'pointer',
                      background: i === activeScreenshot ? 'var(--lp-navy)' : 'transparent',
                      color: i === activeScreenshot ? '#fff' : 'var(--lp-text)',
                      fontSize: 13,
                      fontWeight: i === activeScreenshot ? 600 : 400,
                      fontFamily: 'inherit',
                      transition: 'all 0.15s ease',
                      lineHeight: 1.4,
                    }}
                  >
                    <div style={{ fontWeight: 600, fontSize: 13 }}>{s.title}</div>
                    <div style={{
                      fontSize: 11,
                      opacity: i === activeScreenshot ? 0.75 : 0.55,
                      marginTop: 2,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      maxWidth: 230,
                    }}>
                      {s.caption}
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ═══════ COMPROMISO ═══════ */}
      <section style={{ padding: '80px 0', background: 'var(--lp-navy)', color: '#fff' }}>
        <div ref={ctaReveal.ref} style={{ maxWidth: 720, margin: '0 auto', padding: '0 32px', textAlign: 'center' }}>
          <div className={`reveal-up ${ctaReveal.visible ? 'is-visible' : ''}`}>
            <p style={{ fontSize: 13, fontWeight: 600, letterSpacing: '1px', textTransform: 'uppercase', color: 'var(--lp-accent)', marginBottom: 20 }}>
              Nuestro compromiso
            </p>
            <h2 className="font-display" style={{ fontSize: 'clamp(28px, 4vw, 42px)', lineHeight: 1.2, marginBottom: 20, color: '#fff' }}>
              Que el profesorado dedique su tiempo a lo que importa: enseñar.
            </h2>
            <p style={{ fontSize: 16, lineHeight: 1.7, opacity: 0.7, maxWidth: 520, margin: '0 auto 36px' }}>
              Automatizamos la burocracia académica — generación de actas, cálculo de ponderaciones, envío de boletines — para que cada minuto cuente en la formación de los alumnos.
            </p>
            <div style={{ display: 'flex', gap: 16, justifyContent: 'center', flexWrap: 'wrap' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, fontWeight: 600, padding: '10px 20px', background: 'rgba(255,255,255,0.08)', borderRadius: 8, border: '1px solid rgba(255,255,255,0.12)' }}>
                <span style={{ width: 7, height: 7, borderRadius: '50%', background: 'var(--lp-accent)' }} />
                100% conforme LOMLOE
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, fontWeight: 600, padding: '10px 20px', background: 'rgba(255,255,255,0.08)', borderRadius: 8, border: '1px solid rgba(255,255,255,0.12)' }}>
                <span style={{ width: 7, height: 7, borderRadius: '50%', background: '#6ebb8d' }} />
                Soporte docente directo
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ═══════ CTA FINAL ═══════ */}
      <section style={{ padding: '80px 0', background: 'var(--lp-cream)', textAlign: 'center' }}>
        <div style={{ maxWidth: 600, margin: '0 auto', padding: '0 32px' }}>
          <h2 className="font-display" style={{ fontSize: 'clamp(26px, 3.5vw, 40px)', color: 'var(--lp-navy)', lineHeight: 1.2, marginBottom: 16 }}>
            ¿Listo para simplificar la gestión de tu centro?
          </h2>
          <p style={{ fontSize: 15, color: 'var(--lp-text-secondary)', lineHeight: 1.65, marginBottom: 28 }}>
            Accede al portal académico para comenzar a gestionar calificaciones, boletines y expedientes del alumnado.
          </p>
          <button className="btn-primary" onClick={() => navigate('/login')} style={{ fontSize: 15, padding: '16px 36px' }}>
            Acceder al portal <ArrowRight size={16} />
          </button>
        </div>
      </section>

      {/* ═══════ FOOTER ═══════ */}
      <footer style={{ padding: '32px 0', borderTop: '1px solid var(--lp-border)', background: 'var(--lp-cream)' }}>
        <div style={{ maxWidth: 1140, margin: '0 auto', padding: '0 32px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <img src="/icon.png" alt="Logo" style={{ width: 20, height: 20, opacity: 0.5, filter: 'grayscale(1)' }} />
            <span className="font-display" style={{ fontSize: 14, color: 'var(--lp-text-secondary)' }}>Boletines</span>
            <span style={{ fontSize: 12, color: 'var(--lp-text-secondary)', opacity: 0.6 }}>— Gestión académica para conservatorios y escuelas</span>
          </div>
          <span style={{ fontSize: 12, color: 'var(--lp-text-secondary)', opacity: 0.5 }}>
            © {new Date().getFullYear()} Todos los derechos reservados.
          </span>
        </div>
      </footer>

      {/* ═══════ LIGHTBOX ═══════ */}
      {lightbox && (
        <div className="lightbox-overlay" onClick={() => setLightbox(null)}>
          <div className="lightbox-content" onClick={e => e.stopPropagation()}>
            <button className="lightbox-close" onClick={() => setLightbox(null)}>
              <X size={18} />
            </button>
            <img src={lightbox} alt="Vista ampliada" />
          </div>
        </div>
      )}
    </div>
  );
}
